import { useEffect, useRef, useState } from "react";
import { Animated, StyleSheet, View } from "react-native";
import { ActivityIndicator, Icon, Text } from "react-native-paper";

import { colors, fonts, radius, spacing } from "@/lib/theme";

/**
 * Honest "we're reading it" state for the medications hub.
 *
 * Replaces the previous decorative activity log, which scrolled invented steps
 * ("Running OCR pass 2", "Expanding BID to twice daily") and opened with a
 * hardcoded "Loading photo 1 of 3" no matter how many photos the RN actually
 * took. A nurse who uploaded 23 photos was told the app was loading 3, which is
 * exactly the kind of thing that reads as "the pictures aren't coming up".
 *
 * Everything rendered here is a real value:
 *  - `photoCount` is the number of images attached to the running job (server).
 *  - the elapsed timer counts from the job's own createdAt (server), so it
 *    stays correct when she navigates away and comes back.
 *  - found medications are not claimed here: as soon as the first draft lands
 *    the hub swaps this view out for the real card list.
 *
 * Nothing is extrapolated into a percentage. The worker reports pending /
 * processing / completed plus drafts as they appear, and that is all we know.
 *
 * Dictation runs through the same job and lands on this same screen, so the
 * copy has to follow the source. "23 photos saved / Reading the labels" after
 * she read a list out loud is the same lie in a new costume — and the photo
 * count in particular is left over from her last upload, not from this run.
 */

/** Typical end-to-end extraction, measured from production jobs. */
const TYPICAL_MAX_SECONDS = 60;

export interface ExtractionProgressProps {
    /** Photos attached to the running job. Null when not yet known. */
    photoCount: number | null;
    /** ISO timestamp of the job, from the server. Null falls back to no timer. */
    startedAt: string | null;
    /** What she did to start this. Null when we cannot tell — copy stays neutral. */
    source?: "photo" | "voice" | null;
}

function useElapsedSeconds(startedAt: string | null): number | null {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!startedAt) {
            return;
        }
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [startedAt]);

    if (!startedAt) {
        return null;
    }
    const started = Date.parse(startedAt);
    if (Number.isNaN(started)) {
        return null;
    }
    return Math.max(0, Math.floor((now - started) / 1000));
}

export function ExtractionProgress({
    photoCount,
    startedAt,
    source = null
}: ExtractionProgressProps) {
    const isVoice = source === "voice";
    const elapsed = useElapsedSeconds(startedAt);
    const pulse = useRef(new Animated.Value(0.9)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, { toValue: 1.06, duration: 1100, useNativeDriver: true }),
                Animated.timing(pulse, { toValue: 0.9, duration: 1100, useNativeDriver: true })
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [pulse]);

    const takingLonger = elapsed !== null && elapsed > TYPICAL_MAX_SECONDS;

    return (
        <View style={styles.container}>
            <Animated.View style={[styles.iconGlow, { transform: [{ scale: pulse }] }]}>
                <Icon
                    source={isVoice ? "microphone-message" : "text-recognition"}
                    size={40}
                    color={colors.teal600}
                />
            </Animated.View>

            {/* The reassurance that matters most: her photos are already safe,
                whatever the read does next. */}
            {!isVoice && photoCount !== null && photoCount > 0 && (
                <View style={styles.savedRow}>
                    <Icon source="check-circle" size={18} color={colors.success} />
                    <Text style={styles.savedText}>
                        {photoCount} {photoCount === 1 ? "photo" : "photos"} saved
                    </Text>
                </View>
            )}

            <Text style={styles.title}>
                {isVoice ? "Going through your list" : "Reading the labels"}
            </Text>
            <Text style={styles.subtitle}>
                Medications appear here one by one as they are read.
            </Text>

            <View style={styles.statusRow}>
                <ActivityIndicator size="small" color={colors.teal600} />
                <Text style={styles.statusText}>
                    {elapsed === null
                        ? "Working on it"
                        : takingLonger
                          ? `Still working, ${elapsed}s`
                          : `${elapsed}s of usually under a minute`}
                </Text>
            </View>

            {takingLonger && (
                <View style={styles.noteBox}>
                    <Icon source="information-outline" size={16} color={colors.info600} />
                    <Text style={styles.noteText}>
                        A large batch can take longer. Your photos are saved, so you can keep
                        working and come back to this screen.
                    </Text>
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: spacing.lg
    },
    iconGlow: {
        width: 84,
        height: 84,
        borderRadius: 42,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.primaryLight,
        marginBottom: spacing.lg
    },
    savedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginBottom: spacing.md
    },
    savedText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.success
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    subtitle: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        textAlign: "center",
        marginBottom: spacing.lg
    },
    statusRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    statusText: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textSecondary,
        fontVariant: ["tabular-nums"]
    },
    noteBox: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        marginTop: spacing.lg,
        padding: spacing.md,
        borderRadius: radius.md,
        backgroundColor: colors.infoLight
    },
    noteText: {
        flex: 1,
        fontFamily: fonts.regular,
        fontSize: 12,
        lineHeight: 17,
        color: colors.textSecondary
    }
});
